import { Group } from 'src/design/atoms/Group';
import { Stack } from 'src/design/atoms/Stack';
import {
  IconThumbDown,
  IconThumbDownFilled,
  IconThumbUp,
  IconThumbUpFilled,
} from '@tabler/icons-react';
import React, { useState } from 'react';
import { ChatMessageFeedbackDto } from 'src/api/FiretrolApiClient';
import {
  useDeleteChatMessageFeedback,
  useSetChatMessageFeedback,
} from 'src/api/queries/chat/useChatConversationMutations';
import {
  FEEDBACK_COMMENT_MAX_LENGTH,
  FEEDBACK_RATING_DOWN,
  FEEDBACK_RATING_UP,
  FEEDBACK_REASONS,
} from 'src/app/chat/messageFeedback';
import { BasicActionIcon } from 'src/design/atoms/BasicActionIcon';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { Radio } from 'src/design/atoms/Radio';
import { SecondaryButton } from 'src/design/atoms/SecondaryButton';
import { Textarea } from 'src/design/atoms/Textarea';
import { Popover } from 'src/design/molecules/Popover';

export interface IMessageFeedbackActionsProps {
  conversationId: string;
  messageId: string;
  feedback?: ChatMessageFeedbackDto;
  onPopoverOpenChange?: (opened: boolean) => void;
}

/** @registry Thumbs-up/down vote buttons for an assistant reply; thumbs-down opens a popover for the required reason and an optional comment, and clicking the active thumb removes the vote. */
export const MessageFeedbackActions: React.FC<IMessageFeedbackActionsProps> = ({
  conversationId,
  messageId,
  feedback,
  onPopoverOpenChange,
}) => {
  const setFeedback = useSetChatMessageFeedback();
  const deleteFeedback = useDeleteChatMessageFeedback();
  const [popoverOpened, setPopoverOpenedState] = useState(false);
  const [reason, setReason] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);

  const isUp = feedback?.rating === FEEDBACK_RATING_UP;
  const isDown = feedback?.rating === FEEDBACK_RATING_DOWN;
  const pending = setFeedback.isPending || deleteFeedback.isPending;
  const target = { conversationId, messageId };

  const setPopoverOpened = (opened: boolean) => {
    setPopoverOpenedState(opened);
    onPopoverOpenChange?.(opened);
  };

  const removeVote = () => deleteFeedback.mutate(target);

  const handleUpClick = () => {
    if (isUp) {
      removeVote();
      return;
    }
    setFeedback.mutate({ ...target, request: { rating: FEEDBACK_RATING_UP } });
  };

  const handleDownClick = () => {
    if (isDown) {
      removeVote();
      return;
    }
    setReason(null);
    setComment('');
    setReasonError(null);
    setPopoverOpened(!popoverOpened);
  };

  const handleSubmit = () => {
    if (!reason) {
      setReasonError('Reason is required');
      return;
    }
    setFeedback.mutate({
      ...target,
      request: {
        rating: FEEDBACK_RATING_DOWN,
        reason,
        comment: comment.trim() || undefined,
      },
    });
    setPopoverOpened(false);
  };

  return (
    <>
      <BasicActionIcon
        size="sm"
        variant="subtle"
        color={isUp ? 'blue' : 'gray'}
        label="Good response"
        aria-pressed={isUp}
        disabled={pending}
        onClick={handleUpClick}
      >
        {isUp ? <IconThumbUpFilled size={16} /> : <IconThumbUp size={16} />}
      </BasicActionIcon>
      <Popover
        opened={popoverOpened}
        onChange={setPopoverOpened}
        position="bottom-start"
        withArrow
        shadow="md"
        width={280}
        trapFocus
      >
        <Popover.Target>
          <BasicActionIcon
            size="sm"
            variant="subtle"
            color={isDown ? 'blue' : 'gray'}
            label="Bad response"
            aria-pressed={isDown}
            disabled={pending}
            onClick={handleDownClick}
          >
            {isDown ? (
              <IconThumbDownFilled size={16} />
            ) : (
              <IconThumbDown size={16} />
            )}
          </BasicActionIcon>
        </Popover.Target>
        <Popover.Dropdown>
          <Stack gap="sm">
            <Radio.Group
              label="What went wrong?"
              withAsterisk
              value={reason}
              onChange={(value) => {
                setReason(value);
                setReasonError(null);
              }}
              error={reasonError}
            >
              <Stack gap={6} mt={6}>
                {FEEDBACK_REASONS.map((option) => (
                  <Radio
                    key={option.value}
                    value={option.value}
                    label={option.label}
                  />
                ))}
              </Stack>
            </Radio.Group>
            <Textarea
              label="Comment"
              autosize
              minRows={2}
              maxRows={6}
              maxLength={FEEDBACK_COMMENT_MAX_LENGTH}
              value={comment}
              onChange={(event) => setComment(event.currentTarget.value)}
            />
            <Group justify="flex-end" gap="sm">
              <SecondaryButton
                size="xs"
                onClick={() => setPopoverOpened(false)}
              >
                Cancel
              </SecondaryButton>
              <BasicButton size="xs" onClick={handleSubmit}>
                Submit
              </BasicButton>
            </Group>
          </Stack>
        </Popover.Dropdown>
      </Popover>
    </>
  );
};
